import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, Download } from 'lucide-react';

const MediaViewerModal = ({ src, type, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragY, setDragY] = useState(0);
  const [opacity, setOpacity] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);
  const lastDistance = useRef(null);
  const lastCenter = useRef(null);
  const dragStart = useRef(null);
  const panStart = useRef(null);
  const isVideo = type === 'video';

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = useCallback(() => {
    setVisible(false);
    setTimeout(onClose, 200);
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Distance between two touch points
  const getDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const getCenter = (t1, t2) => ({
    x: (t1.clientX + t2.clientX) / 2,
    y: (t1.clientY + t2.clientY) / 2,
  });

  const handleTouchStart = (e) => {
    if (isVideo) return;
    if (e.touches.length === 2) {
      // Pinch start
      lastDistance.current = getDistance(e.touches[0], e.touches[1]);
      lastCenter.current = getCenter(e.touches[0], e.touches[1]);
      panStart.current = { x: translate.x, y: translate.y };
    } else if (e.touches.length === 1 && scale <= 1) {
      // Single finger drag (swipe to close) only when not zoomed
      dragStart.current = { y: e.touches[0].clientY, time: Date.now() };
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e) => {
    if (isVideo) return;
    if (e.touches.length === 2 && lastDistance.current !== null) {
      // Pinch zoom
      const dist = getDistance(e.touches[0], e.touches[1]);
      const center = getCenter(e.touches[0], e.touches[1]);
      const newScale = Math.max(1, Math.min(5, scale * (dist / lastDistance.current)));
      // Pan while pinching
      if (lastCenter.current && panStart.current) {
        const dx = center.x - lastCenter.current.x;
        const dy = center.y - lastCenter.current.y;
        setTranslate({ x: panStart.current.x + dx, y: panStart.current.y + dy });
      }
      setScale(newScale);
      lastDistance.current = dist;
    } else if (e.touches.length === 1 && dragStart.current && scale <= 1) {
      // Swipe to close
      const dy = e.touches[0].clientY - dragStart.current.y;
      setDragY(dy);
      setOpacity(Math.max(0.3, 1 - Math.abs(dy) / 400));
    }
  };

  const handleTouchEnd = (e) => {
    if (isVideo) return;
    // Pinch end
    if (e.touches.length < 2) {
      lastDistance.current = null;
      lastCenter.current = null;
      panStart.current = null;
      // Snap back if scale is close to 1
      if (scale < 1.05) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      }
    }
    // Swipe-to-close end
    if (e.touches.length === 0 && dragStart.current && scale <= 1) {
      const elapsed = Date.now() - dragStart.current.time;
      const velocity = Math.abs(dragY) / elapsed;
      if (Math.abs(dragY) > 120 || velocity > 0.5) {
        handleClose();
      } else {
        setDragY(0);
        setOpacity(1);
      }
      dragStart.current = null;
      setIsDragging(false);
    }
  };

  // Mouse wheel zoom for desktop
  const handleWheel = (e) => {
    if (isVideo) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale((s) => Math.max(1, Math.min(5, s * delta)));
    if (scale * delta <= 1.05) setTranslate({ x: 0, y: 0 });
  };

  // Double-tap to zoom
  const lastTap = useRef(0);
  const handleDoubleTap = (e) => {
    if (isVideo) return;
    const now = Date.now();
    if (now - lastTap.current < 300) {
      if (scale > 1) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      } else {
        setScale(2.5);
      }
    }
    lastTap.current = now;
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = src;
    a.download = `callchat-media.${isVideo ? 'mp4' : 'jpg'}`;
    a.target = '_blank';
    a.click();
  };

  const mediaTransform = `translateY(${dragY}px) scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`;

  return (
    <div
      className={`fixed inset-0 z-[99999] flex items-center justify-center transition-opacity duration-200 ${visible ? 'opacity-100' : 'opacity-0'}`}
      data-testid="media-viewer-modal"
    >
      <div className="absolute inset-0 bg-black" style={{ opacity }} />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 py-2 bg-gradient-to-b from-black/60 to-transparent">
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-full text-white/90 hover:text-white transition-colors"
          data-testid="media-viewer-close"
        >
          <X className="w-6 h-6" />
        </button>
        <button
          onClick={handleDownload}
          className="w-10 h-10 flex items-center justify-center rounded-full text-white/90 hover:text-white transition-colors"
          data-testid="media-viewer-download"
        >
          <Download className="w-5 h-5" />
        </button>
      </div>

      {/* Media container */}
      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onWheel={handleWheel}
        onClick={(e) => {
          // Close on background tap (not on media)
          if (e.target === containerRef.current) handleClose();
          handleDoubleTap(e);
        }}
      >
        {isVideo ? (
          <video
            src={src}
            className="max-w-[95vw] max-h-[85vh] object-contain"
            controls
            autoPlay
            playsInline
            onClick={(e) => e.stopPropagation()}
            data-testid="media-viewer-video"
          />
        ) : (
          <img
            src={src}
            alt="Full view"
            className="max-w-[95vw] max-h-[85vh] object-contain select-none"
            style={{
              transform: mediaTransform,
              transition: isDragging || scale > 1 ? 'none' : 'transform 0.2s ease',
              touchAction: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
            draggable={false}
            data-testid="media-viewer-image"
          />
        )}
      </div>
    </div>
  );
};

export default MediaViewerModal;
